import PropTypes from "prop-types";
import React, {useCallback, useEffect, useRef, useState} from "react";
import "./style.scss";
import {Navigation} from "../Navigation";
import {NavigationEnum} from "../../enum/NavigationEnum";
import {camelToSnakeCase, getBrowserWidth, getQueryString, relocateToPage} from "../../utils/common";
import {User} from "../../domin/pay/User";
import {getH5ResultRequest, initialiseRequest} from "../../utils/api";
import {Page} from "./Page";
import {Tracking} from "../../tracking/tracking";
import {clearReferrers, updateReferrers} from "../../utils/localStorage";
import {imagePreloadService} from "../../services/ImagePreloadService";
import {isProd} from "../../config/config";
import {FROM_EMAIL_71_DISCOUNT, MODE, QUESTIONNAIRE_SUBMIT_LOGGED} from "../../const/LocalStorage";
const track = new Tracking("en");
let startScroll = false;
let scrollBottom = false;


export const PageController = ({pages, localStorageKey, formData, mode = ""}) => {

    const [addClass, setAddClass] = useState("");
    const [displayNav, setDisplayNav] = useState(false);
    const [isMobile, setIsMobile] = useState(getBrowserWidth() < 768);
    const [globalClicked, setGlobalClicked] = useState(false);
    const cloneFormData = useCallback((sourceData) => {
        if (!sourceData) {
            return formData;
        }
        if (typeof sourceData.clone === "function") {
            return sourceData.clone();
        }
        const plainData = sourceData?.toPlainObject ? sourceData.toPlainObject() : sourceData;
        return formData.fromPlainObject(plainData);
    }, [formData]);

    const loadInitialFormDataFromLocalStorage = useCallback( () => {
        try {
            const savedData = localStorage.getItem(localStorageKey);
            if (savedData) {
                return cloneFormData(JSON.parse(savedData));
            }
        } catch (error) {
            console.error("Failed to load data from localStorage:", error);
        }
        return formData;
    }, [cloneFormData, formData, localStorageKey]);

    const isPageVisible = useCallback((page, candidateData) => {
        if (!page) {
            return false;
        }

        if (typeof page.shouldShow !== "function") {
            return true;
        }

        try {
            return !!page.shouldShow(candidateData);
        } catch (error) {
            console.warn(`Failed to evaluate shouldShow for page "${page.pageId}"`, error);
            return true;
        }
    }, []);

    const findVisiblePageIndex = useCallback((startIndex, step, candidateData, includeStart = false) => {
        let pageIndex = includeStart ? startIndex : startIndex + step;

        while (pageIndex >= 0 && pageIndex < pages.length) {
            if (isPageVisible(pages[pageIndex], candidateData)) {
                return pageIndex;
            }
            pageIndex += step;
        }

        return -1;
    }, [isPageVisible, pages]);

    const loadByUserSurveyId = async () =>{
        const userSurveyId = getQueryString("user_survey_id");
        let user = new User();
        user.setDeviceToken(userSurveyId);
        await initialiseRequest();
        
        // Non-VIP user, load questionnaire data and continue to pay page
        const res = await getH5ResultRequest();
        const savedDataString = res?.response?.result_data;
        let saveData = JSON.parse(savedDataString);
        saveData = saveData?.questionnaire_responses;
        const saveFormData = cloneFormData(saveData);
        saveFormData.curIndex = 0;
        setData(saveFormData);
        
        // Save to localStorage for later use
        localStorage.setItem(localStorageKey, JSON.stringify(saveData));
        
        // 标记从邮件进来，邮件承诺了71%优惠
        localStorage.setItem(FROM_EMAIL_71_DISCOUNT, 'true');

        // Check if user is already VIP, if so, redirect to download or signup page
        if (user.isVip()) {
            // VIP user, redirect based on email status
            if (user.isGuestUser()) {
                // Guest user, go to download page
                relocateToPage("/signup");
            } else {
                relocateToPage("/download");
            }
            return;
        }
    }

    const [data, setData] = useState(loadInitialFormDataFromLocalStorage);

    useEffect(() => {

        clearReferrers();
        updateReferrers("survey");
        localStorage.setItem(MODE, mode);
        async function fetchData() {
            //加载问卷id
            if (getQueryString("user_survey_id")) {
                await loadByUserSurveyId();
            }

            // 预加载关键图片
            if (mode) {
                imagePreloadService.preloadQuestionnaireImages(mode).catch(error => {
                    console.warn("Failed to preload some images:", error);
                });
            }

            // 将track实例暴露到全局，供gtag使用
            window.track = track;
            if (data.curIndex === 0 || !data.curIndex) {
                track.logEventInstantly("surveylandingitem_exposure", {
                    value: data?.age || "unknown",
                    index: (data?.curIndex || 0) + ""
                }, "survey");
            }
        }
        fetchData();


        //监听浏览器宽高变化
        const handleResize = () => {
            setIsMobile(getBrowserWidth() < 981);
        };

        // 滚动埋点处理
        const handleScroll = (event) => {
            const scrollContainer = event.target;
            const scrollTop = scrollContainer.scrollTop;
            const scrollHeight = scrollContainer.scrollHeight;
            const clientHeight = scrollContainer.clientHeight;

            // 首次滚动埋点
            if (!startScroll) {
                startScroll = true;
                track.logEvent("page_scroll", {}, "survey");
            }

            // 滚动到底部埋点
            if (!scrollBottom && scrollTop + clientHeight > scrollHeight - 30) {
                scrollBottom = true;
                track.logEvent("page_scrolltobottom", {}, "survey");
            }
        };

        // 获取滚动容器并添加事件监听器
        const addScrollListeners = () => {
            const scrollContainers = document.querySelectorAll('.gallery-pages-item');
            scrollContainers.forEach(container => {
                container.addEventListener("scroll", handleScroll);
            });
        };
        
        // 延迟添加滚动监听器，确保DOM已渲染
        setTimeout(addScrollListeners, 100);
        
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            // 清理滚动监听器
            const scrollContainers = document.querySelectorAll('.gallery-pages-item');
            scrollContainers.forEach(container => {
                container.removeEventListener("scroll", handleScroll);
            });
            // 重置状态
            startScroll = false;
            scrollBottom = false;
        };
    },[])


    const [navigationStatus, setNavigationStatus] = useState(pages?.[data?.curIndex]?.navigationStatus || NavigationEnum.SHOW_ICON);

    const updateFormDataField = useCallback((index ,field = "", value = "" ) => {
        setData(prevData => {
            const newInstance = cloneFormData(prevData);
            newInstance["curIndex"] = index;
            if (field) {
                newInstance[field] = value;
            }
            return newInstance;
        });
    }, [cloneFormData]);

    useEffect(() => {
        if (!data || isPageVisible(pages[data.curIndex], data)) {
            return;
        }

        const nextPageIndex = findVisiblePageIndex(data.curIndex, 1, data);
        const prevPageIndex = findVisiblePageIndex(data.curIndex, -1, data);
        const fallbackIndex = nextPageIndex !== -1 ? nextPageIndex : (prevPageIndex !== -1 ? prevPageIndex : 0);

        if (fallbackIndex !== data.curIndex) {
            updateFormDataField(fallbackIndex);
        }
    }, [data, findVisiblePageIndex, isPageVisible, pages, updateFormDataField]);

    useEffect(() => {
        if (!data) {
            return;
        }
        localStorage.setItem(localStorageKey, JSON.stringify(data.toPlainObject()));
        
        // Only log questionnairesubmit_click once (check localStorage)
        const questionnaireSubmitLogged = localStorage.getItem(QUESTIONNAIRE_SUBMIT_LOGGED);
        if (data.curIndex === pages.length - 1 && !questionnaireSubmitLogged) {
            logEvent("questionnairesubmit_click", {
                content: data.toPlainObject()
            })
            localStorage.setItem(QUESTIONNAIRE_SUBMIT_LOGGED, 'true');
        }

    }, [data])


    const updateNavigationStatus = (status, next = false) => {
        if (status !== navigationStatus) {
            if (next) {
                setNavigationStatus(NavigationEnum.SLIDE_LEFT)
            } else {
                setNavigationStatus(NavigationEnum.SLIDE_RIGHT);
            }
            setTimeout(() => {
                setNavigationStatus(status);
            }, 300)
        }
    }

    const clickNext = (key, value = "", nextId = "") => {
        const curIndex = data.curIndex;
        if (addClass === "") {
            const nextData = cloneFormData(data);
            if (key) {
                nextData[key] = value;
            }

            let nextPageIndex = -1;
            if (nextId) {
                const requestedIndex = pages.findIndex(page => page.pageId === nextId);
                if (requestedIndex !== -1) {
                    nextPageIndex = isPageVisible(pages[requestedIndex], nextData)
                        ? requestedIndex
                        : findVisiblePageIndex(requestedIndex, 1, nextData);
                }
            } else {
                nextPageIndex = findVisiblePageIndex(curIndex, 1, nextData);
            }

            if (nextPageIndex === -1) {
                return;
            }

            setAddClass("next");
            updateFormDataField(curIndex, key, value);
            logEvent("next_click", {
                name: camelToSnakeCase(key),
                string1: typeof value === 'string' ? value : JSON.stringify(value)
            });

            logLayoutScroll(curIndex, nextPageIndex, 1);

            updateNavigationStatus(pages[nextPageIndex]?.navigationStatus, true);
            setTimeout(() => {
                setAddClass("");
                updateFormDataField(nextPageIndex, key, value);
            }, 500)
        }
    }
    const clickPrev = (key) => {
        const curIndex = data.curIndex;
        if (addClass === "") {
            const prevPageIndex = findVisiblePageIndex(curIndex, -1, data);
            if (prevPageIndex === -1) {
                return;
            }

            setAddClass("prev");
            logEvent("prev_click", {
                name: camelToSnakeCase(key)
            })

            logLayoutScroll(curIndex, prevPageIndex, -1);

            updateNavigationStatus(pages[prevPageIndex]?.navigationStatus, false);
            setTimeout(() => {
                setAddClass("");
                updateFormDataField(prevPageIndex);
            }, 500)
        }
    }
    const logEvent = (eventName, data, logPrefix = "survey") => {
        track.logEvent(eventName, data, logPrefix);
    };
    
    // 页面切换埋点方法
    const logLayoutScroll = (fromIndex, toIndex, direction) => {
        const moveLayoutId = pages[fromIndex]?.pageId || "";
        const nextLayoutId = pages[toIndex]?.pageId || "";
        
        logEvent("layout_scroll", {
            string1: moveLayoutId,
            string2: nextLayoutId,
            string3: direction > 0 ? "right" : "left",
            type: pages[fromIndex]?.pageId || "",
            value: pages[fromIndex]?.pageId || ""
        });
    };

    if (!data) {
        return ;
    }

    const visiblePageIndexes = pages.reduce((indexes, page, index) => {
        if (isPageVisible(page, data)) {
            indexes.push(index);
        }
        return indexes;
    }, []);
    const visibleCurIndex = visiblePageIndexes.indexOf(data.curIndex);
    const visiblePages = visiblePageIndexes.map(index => pages[index]);

    return (
        <div className={'gallery-pages'}>
            {!isProd() && <div className={'clear-btn'} onClick={() => {
                if (window.confirm('Are you sure you want to clear cache and reload?')) {
                    localStorage.clear(); 
                    location.reload();
                }
            }} >Clear Cache</div>}

            <Navigation
                data={visiblePages}
                curIndex={visibleCurIndex === -1 ? 0 : visibleCurIndex}
                back={clickPrev}
                status={navigationStatus}
                displayNav={displayNav}
                setDisplayNav={setDisplayNav}
                isMobile={isMobile}
            />

        <div className={`gallery-pages-wrap ${addClass}`}>
            {visibleCurIndex === 0 && <a className="gallery-pages-scroll"/>}
            {visiblePages.map((page, index) => {
                if (visibleCurIndex === 0 && index > 3) {
                    return null;
                }
                if (visibleCurIndex > 0 && (index < visibleCurIndex - 1 || index > visibleCurIndex + 3)) {
                    return null;
                }
                const pageProps = page.props || {};

                return <Page
                    key={page.pageId || index}
                    setDisplayNav={setDisplayNav}
                    isCurPage={visibleCurIndex === index}
                    data={data}
                    page={page}
                    value={data[page.pageId] || ""}
                    logEvent={logEvent}
                    isMobile={isMobile}
                    globalClicked={globalClicked}
                    setGlobalClicked={setGlobalClicked}
                    clickNext={(value, nextId = "") => clickNext(page.pageId, value, nextId)}
                    clickPrev={() => {clickPrev(page.pageId)}}
                    {...pageProps}
                />
            })}
        </div>
        </div>
    );
};

PageController.propTypes = {};
